import React from 'react'
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  Caption1,
  Text,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import { PinRegular, PinOffRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL, tokens.spacingVerticalXS),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  // The product mark and name sit on one line, as Office task panes title themselves.
  brand: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalSNudge,
    minWidth: 0,
  },
  logo: { flexShrink: 0, width: '20px', height: '20px', display: 'block' },
  name: {
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  spacer: { flexGrow: 1 },
  user: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    maxWidth: '45%',
  },
  crumbs: { minWidth: 0 },
})

export interface PaneHeaderProps {
  title: string
  subtitle?: string
  /**
   * Where the user is, ending with the current place. The last entry is shown as the
   * current page and is not clickable.
   */
  trail?: { key: string; label: string; onClick?: () => void }[]
  /** Whether the add-in is pinned open; omit to hide the pin control. */
  pinned?: boolean
  onTogglePin?: () => void
}

/**
 * Task pane header: the product mark and name on one line, the signed-in user beside
 * them, and a breadcrumb underneath saying where in the add-in the user is.
 */
export const PaneHeader: React.FC<PaneHeaderProps> = ({
  title,
  subtitle,
  trail,
  pinned,
  onTogglePin,
}) => {
  const styles = useStyles()

  return (
    <header className={styles.root}>
      <div className={styles.brand}>
        <svg className={styles.logo} viewBox="0 0 28 28" role="img" aria-label={`${title} logo`}>
          <rect width="28" height="28" rx="6" fill={tokens.colorBrandBackground} />
          <path d="M7 9.5h14v3h-5.5V21h-3v-8.5H7z" fill={tokens.colorNeutralForegroundOnBrand} />
        </svg>

        <Text weight="semibold" size={400} className={styles.name}>
          {title}
        </Text>

        <span className={styles.spacer} />

        {subtitle && (
          <Caption1 className={styles.user} title={subtitle}>
            {subtitle}
          </Caption1>
        )}

        {onTogglePin && (
          <Tooltip content={pinned ? 'Unpin this pane' : 'Keep this pane open'} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={pinned ? <PinOffRegular /> : <PinRegular />}
              onClick={onTogglePin}
              aria-pressed={pinned}
            />
          </Tooltip>
        )}
      </div>

      {trail && trail.length > 0 && (
        <Breadcrumb className={styles.crumbs} size="small" aria-label="Location">
          {trail.map((crumb, index) => {
            const last = index === trail.length - 1
            return (
              <React.Fragment key={crumb.key}>
                {index > 0 && <BreadcrumbDivider />}
                <BreadcrumbItem>
                  <BreadcrumbButton
                    current={last}
                    onClick={last ? undefined : crumb.onClick}
                    disabled={last || !crumb.onClick}
                  >
                    {crumb.label}
                  </BreadcrumbButton>
                </BreadcrumbItem>
              </React.Fragment>
            )
          })}
        </Breadcrumb>
      )}
    </header>
  )
}
